import { useState } from 'react'
import { Alert, App, AutoComplete, Button, Card, Empty, Form, Modal, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { translateError } from '@/shared/api/errors'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { activeTimeColumns, networkColumns, trafficColumns } from './SessionColumns'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  applyRateLimit,
  disconnectSession,
  disconnectUser,
  fetchActiveSessionsOf,
  type ActiveSession,
} from './sessionApi'

interface SubscriberSessionsProps {
  username: string
  /**
   * Karta HOZIR ko'rinib turibdimi (kartochkadagi faol bo'lim).
   *
   * ⚠️ `false` bo'lganda so'rov UMUMAN yuborilmaydi va 15 soniyalik davr
   * ham to'xtaydi. Faol sessiyalar `radacct` ustidagi og'ir so'rov, va
   * operator boshqa bo'limda ishlayotganda uni daqiqasiga to'rt marta
   * takrorlash sof isrof edi.
   *
   * ⚠️ Keshdagi ma'lumot YO'QOLMAYDI: bo'limga qaytilganda jadval darhol
   * eski holatni ko'rsatadi va fonda yangilanadi.
   *
   * Standart `true` — chaqiruvchi bo'lim tushunchasini bilmasa xatti-harakat
   * o'zgarmasin.
   */
  active?: boolean
}

/** Tez tanlash uchun MikroTik shaklidagi tayyor tezliklar */
const RATE_PRESETS = ['128k/128k', '5M/5M', '10M/10M', '50M/50M', '100M/100M'] as const

const RATE_PATTERN = /^[0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?$/

/**
 * Abonentning FAOL sessiyalari va ular ustidagi amallar.
 *
 * ⚠️ BU YERDAGI AMALLAR BAZANI O'ZGARTIRMAYDI (CLAUDE.md §5.5).
 * "Uzish" — shunchaki sessiyani to'xtatish; abonent qayta ulanadi va
 * bazadagi holatni oladi. "Tezlikni qo'llash" ham FAQAT hozir ulangan
 * qurilmalarga ta'sir qiladi — doimiy tezlik siyosatda (Policy) saqlanadi
 * va uni Tariflar sahifasida o'zgartiriladi.
 *
 * Shuning uchun bu karta "tezlikni o'zgartirish" emas, "o'zgargan tezlikni
 * yetkazish" deb nomlangan — aks holda operator bu yerdan qilingan
 * o'zgarish saqlanadi deb o'ylardi.
 */
export function SubscriberSessions({ username, active = true }: SubscriberSessionsProps) {
  const { t } = useTranslation()
  const [rateOpen, setRateOpen] = useState(false)
  const [form] = Form.useForm<{ rateLimit: string }>()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  // Turg'un oyna (tezlik) React daraxti ICHIDA — unga bu tegishli emas.
  const { modal } = App.useApp()

  const runner = useOperationRunner([
    queryKeys.sessions.activeOf(username),
    queryKeys.sessions.onlineLogins,
    queryKeys.sessions.all,
  ])

  const sessions = useQuery({
    queryKey: queryKeys.sessions.activeOf(username),
    queryFn: () => fetchActiveSessionsOf(username),
    // Faol sessiya tez o'zgaradi — bo'lim ochiq turganda yangilanib tursin
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
    // ⚠️ Bo'lim yopiq bo'lsa so'rov ham, davr ham to'xtaydi (prop javadoc'i)
    enabled: active,
    // Davr bilan bir xil chegara: bo'limlar orasida yurib qaytganda davr
    // o'rtasida qo'shimcha so'rov ketmasin
    staleTime: 15_000,
  })

  const rows = sessions.data ?? []

  const columns: ColumnsType<ActiveSession> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    ...networkColumns<ActiveSession>(t),
    ...activeTimeColumns(t),
    ...trafficColumns<ActiveSession>(t),
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'disconnect',
              label: t('actions.disconnect'),
              permission: 'subscriber.session.disconnect',
              danger: true,
              loading: runner.running,
              onClick: () => {
                modal.confirm({
                  title: t('actions.disconnectSessionTitle'),
                  /*
                    ⚠️ O'RINGA QO'YISH ARGUMENTLARI — ular UNUTILGAN EDI va
                    natijasi ekranda ko'rinardi: tasdiq oynasida xom
                    «{{username}} abonentining sessiyasi ({{ip}}) uziladi»
                    turardi. i18next mos qiymat berilmasa naqshni O'Z HOLICHA
                    qoldiradi — ya'ni xato jimgina, kompilyator ham, sinovlar
                    ham hech nima demaydi.

                    ⚠️ IP noma'lum bo'lishi mumkin (`framedIp: string | null`) —
                    o'shanda chiziqcha qo'yiladi, "null" degan so'z emas.
                  */
                  content: t('actions.disconnectSessionConfirm', {
                    username: row.username,
                    ip: row.framedIp ?? t('common.dash'),
                  }),
                  okButtonProps: { danger: true },
                  okText: t('actions.disconnect'),
                  cancelText: t('common.cancel'),
                  onOk: () => runner.run(() => disconnectSession(row.radacctId)),
                })
              },
            },
          ]}
        />
      ),
    },
  ]

  const submitRate = () => {
    submitOnValid(form, async (values) => {
      const outcome = await runner.run(() => applyRateLimit(username, values.rateLimit))
      if (outcome.status !== 'FAILED') {
        setRateOpen(false)
        form.resetFields()
      }
    })
  }

  return (
    <Card
      title={t('subscriber.activeSession')}
      extra={
        <Space>
          <Can permission="subscriber.ratelimit.set">
            <Button size="small" disabled={rows.length === 0} onClick={() => setRateOpen(true)}>
              {t('actions.rateLimit.button')}
            </Button>
          </Can>
          <Can permission="subscriber.session.disconnect">
            <Button
              size="small"
              danger
              disabled={rows.length === 0}
              loading={runner.running}
              onClick={() =>
                modal.confirm({
                  title: t('actions.disconnectTitle'),
                  // Yuqoridagi izohga qarang — bu yerda ham argument unutilgan edi
                  content: t('actions.disconnectConfirm', { username }),
                  okButtonProps: { danger: true },
                  okText: t('actions.disconnect'),
                  cancelText: t('common.cancel'),
                  onOk: () => runner.run(() => disconnectUser(username)),
                })
              }
            >
              {t('actions.disconnectAll')}
            </Button>
          </Can>
        </Space>
      }
    >
      {/*
        ⚠️ NATIJA KARTADA FAQAT TEZLIK OYNASI YOPIQ BO'LGANDA (BUG-006).

        `submitRate` `FAILED` da oynani ATAYLAB ochiq qoldiradi, natija
        esa faqat shu yerda chizilardi — `.ant-modal-mask` ning ortida.
        Ya'ni «MikroTik qabul qilmadi» degan javob operatorga umuman
        ko'rinmasdi va u aynan shu oynada qiymatni tuzatishi kerak edi.

        Uzish amallari (`Modal.confirm`) bu shartga tushmaydi: ular
        yopilgandan KEYIN natija beradi, ya'ni oyna baribir yopiq.
      */}
      {runner.result && !rateOpen ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}

      {/*
        ⚠️ XATO HOLATI — usiz bu karta "abonent oflayn" deb YOLG'ON
        gapirardi.

        Bu yerda yolg'on ayniqsa qimmat: operator abonent shikoyati
        bilan aynan shu kartaga qaraydi. "Sessiya yo'q" degan xulosa
        uni butunlay boshqa yo'nalishga — parol, kabel, MikroTik
        sozlamalari tomonga — boshlab ketardi, holbuki abonent
        onlayn, faqat panel ma'lumotni ololmagan.
      */}
      {sessions.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(sessions.error, t)}
          action={
            <Button size="small" onClick={() => void sessions.refetch()}>
              {t('dataTable.retry')}
            </Button>
          }
        />
      ) : (
        <Table<ActiveSession>
          columns={columns}
          dataSource={rows}
          rowKey={(row) => row.radacctId}
          loading={sessions.isPending}
          size="small"
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('subscriber.noActiveSession')}
              />
            ),
          }}
        />
      )}

      <Modal
        open={rateOpen}
        title={t('actions.rateLimit.title')}
        onCancel={() => {
          runner.clear()
          setRateOpen(false)
        }}
        onOk={submitRate}
        confirmLoading={runner.running}
        okText={t('actions.rateLimit.apply')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/* ⚠️ XATO SHU YERDA — tezlik maydoni bilan BIR EKRANDA (BUG-006). */}
        {runner.result ? (
          <div style={{ marginBottom: 16 }}>
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          </div>
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="rateLimit"
            label={t('tariffs.rateLimit')}
            // ⚠️ Bu o'zgarish SAQLANMAYDI: endpoint faqat faol sessiyaga
            // qo'llaydi. Doimiy tezlik siyosatda — buni aytmaslik eng katta
            // chalkashlik manbai bo'lardi
            extra={t('actions.rateLimit.notPersistedHint')}
            rules={[
              { required: true, message: t('actions.rateLimit.validationRequired') },
              { pattern: RATE_PATTERN, message: t('actions.rateLimit.validationFormat') },
            ]}
          >
            {/* `AutoComplete`, `Select mode="tags"` EMAS: ikkinchisi qiymatni
                MASSIV qilib qaytaradi va forma qiymati `string[]` bo'lib,
                validatsiya naqshi hech qachon mos kelmasdi. `AutoComplete`
                esa satr beradi va ro'yxatda yo'q tezlikni yozishga ham
                ruxsat qiladi (MikroTik ixtiyoriy qiymatni qabul qiladi). */}
            <AutoComplete
              autoFocus
              options={RATE_PRESETS.map((value) => ({ value }))}
              placeholder="10M/10M"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}
